import { useCallback, useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { ArrowLeft, BarChart3 } from "lucide-react";
import { useAuth } from "@/lib/auth";
import { wardScope } from "@/lib/patient";
import { Cell, DataTable, MetricCard, PageHeader, Panel, PanelHeader, Row } from "./ui";

const Field = ({ label, children }) => (
  <label className="flex min-w-0 flex-col gap-1 text-[13px] text-ink-600">
    {label}
    {children}
  </label>
);

const inputClass =
  "h-11 w-full min-w-0 rounded-xl border border-border bg-white px-3.5 text-[14px] text-ink-800 focus:border-sage-600 focus:outline-none focus:ring-2 focus:ring-sage-600/15";

const WARD_LABELS = { all: "Todas as alas", MASCULINA: "Ala Masculina", FEMININA: "Ala Feminina" };

const Bars = ({ rows }) => {
  const max = Math.max(...rows.map((r) => r.count), 1);
  return (
    <div data-testid="discharge-metrics-chart" className="space-y-3">
      {rows.map((row) => (
        <div key={row.type}>
          <div className="flex items-baseline justify-between gap-3 text-[14.5px]">
            <span className="text-ink-700">
              {row.label}
              {row.informative ? " (informativo)" : ""}
            </span>
            <span className="font-semibold text-ink-900">
              {row.count} · {row.percent}%
            </span>
          </div>
          <div className="mt-1.5 h-2 rounded-full bg-sand">
            <div
              className={`h-2 rounded-full ${row.informative ? "bg-ink-400" : "bg-sage-600"}`}
              style={{ width: `${(row.count / max) * 100}%` }}
            />
          </div>
        </div>
      ))}
    </div>
  );
};

export default function DischargeMetrics() {
  const { api, user } = useAuth();
  const scope = wardScope(user);
  const [range, setRange] = useState({ start: "", end: "", ward: scope.value });
  const [data, setData] = useState(null);

  const load = useCallback(async () => {
    setData(null);
    try {
      const { data: payload } = await api.get("/portal/metrics/discharges", {
        params: { start: range.start || undefined, end: range.end || undefined, ward: range.ward || "all" },
      });
      setData(payload);
    } catch {
      setData(false);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [range.start, range.end, range.ward]);

  useEffect(() => {
    load();
  }, [load]);

  const rows = data?.by_type || [];

  return (
    <div data-testid="discharge-metrics-page" className="space-y-6">
      <Link
        to="/portal/metricas"
        data-testid="discharge-metrics-back"
        className="inline-flex items-center gap-2 text-[14px] font-semibold text-ink-700 hover:text-ink-900"
      >
        <ArrowLeft className="h-4 w-4" /> Voltar para métricas
      </Link>
      <PageHeader
        kicker="Altas"
        title="Métricas de alta"
        subtitle="Somente altas efetivas; a previsão de alta não entra nestes números."
      />

      <Panel testId="discharge-metrics-filters" className="space-y-4">
        <PanelHeader
          label="Período e ala"
          title="Filtro pela data efetiva da alta e pela ala da internação encerrada"
          hint={
            data
              ? `Período aplicado: ${data.period.start || "início dos registros"} a ${data.period.end || "hoje"} (limites inclusivos) · ${
                  scope.locked ? scope.label : WARD_LABELS[range.ward] || "Todas as alas"
                }.`
              : "Carregando…"
          }
          actions={
            <div className="flex w-full min-w-0 flex-wrap items-end gap-2">
              <Field label="Data inicial">
                <input
                  type="date"
                  data-testid="discharge-metrics-start"
                  value={range.start}
                  onChange={(e) => setRange({ ...range, start: e.target.value })}
                  className={inputClass}
                />
              </Field>
              <Field label="Data final">
                <input
                  type="date"
                  data-testid="discharge-metrics-end"
                  value={range.end}
                  onChange={(e) => setRange({ ...range, end: e.target.value })}
                  className={inputClass}
                />
              </Field>
              {scope.locked ? (
                <span
                  data-testid="discharge-metrics-ward-scope"
                  className="inline-flex h-11 items-center rounded-xl bg-sand px-3.5 text-[14px] font-semibold text-ink-700"
                >
                  {scope.label}
                </span>
              ) : (
                <Field label="Ala">
                  <select
                    data-testid="discharge-metrics-ward"
                    value={range.ward}
                    onChange={(e) => setRange({ ...range, ward: e.target.value })}
                    className={inputClass}
                  >
                    <option value="all">Todas as alas</option>
                    <option value="MASCULINA">Masculina</option>
                    <option value="FEMININA">Feminina</option>
                  </select>
                </Field>
              )}
            </div>
          }
        />
      </Panel>

      <section className="grid gap-4 sm:grid-cols-2 xl:grid-cols-4">
        <MetricCard testId="discharge-metric-total" label="Altas no período" value={data?.total ?? 0} icon={BarChart3} />
        <MetricCard
          testId="discharge-metric-average"
          label="Duração média (dias)"
          value={data?.average_days ?? "indisponível"}
          hint={`${data?.average_sample ?? 0} episódio(s) na média`}
        />
        <MetricCard
          testId="discharge-metric-excluded"
          label="Fora da média"
          value={data?.excluded_incomplete ?? 0}
          hint="Episódios com datas incompletas"
        />
        <MetricCard
          testId="discharge-metric-undated"
          label="Altas sem data"
          value={data?.undated_discharges?.length ?? 0}
          hint="Pendências fora do filtro por data"
          alert={(data?.undated_discharges?.length ?? 0) > 0}
        />
      </section>

      <Panel testId="discharge-metrics-panel" className="space-y-4">
        <PanelHeader label="Distribuição" title="Altas por tipo" hint="Percentual sobre todas as altas efetivas do período." />
        {data === false ? (
          <p className="text-[14px] text-ink-500">Não foi possível carregar as métricas agora.</p>
        ) : data === null ? (
          <p data-testid="discharge-metrics-loading" className="text-[14px] text-ink-500">
            Carregando métricas…
          </p>
        ) : data.total === 0 ? (
          <p data-testid="discharge-metrics-empty" className="text-[14px] text-ink-500">
            Nenhuma alta encontrada para os filtros selecionados.
          </p>
        ) : (
          <>
            <DataTable
              testId="discharge-metrics-table"
              columns={[
                { key: "type", label: "Tipo" },
                { key: "count", label: "Altas" },
                { key: "percent", label: "%" },
                { key: "avg", label: "Duração média (dias)" },
                { key: "sample", label: "Episódios na média" },
              ]}
            >
              {rows.map((row) => (
                <Row key={row.type} testId={`discharge-metrics-row-${row.type}`}>
                  <Cell>
                    {row.label}
                    {row.informative ? " (informativo)" : ""}
                  </Cell>
                  <Cell>{row.count}</Cell>
                  <Cell>{row.percent}%</Cell>
                  <Cell>{row.average_days ?? "—"}</Cell>
                  <Cell>{row.average_sample}</Cell>
                </Row>
              ))}
            </DataTable>
            <Bars rows={rows} />
          </>
        )}
      </Panel>
    </div>
  );
}
